import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip,
  useTheme, Slider, FormControl, InputLabel, Select, MenuItem, Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TimelineIcon from '@mui/icons-material/Timeline';
import DownloadIcon from '@mui/icons-material/Download';
import SpeedIcon from '@mui/icons-material/Speed';
import PlaceIcon from '@mui/icons-material/Place';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  readFileAsText, parseAnyGeoFile, haversineDistance, calculateSpeed, detectStops,
} from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import L from 'leaflet';

// ── Canvas Speed Chart ──
function SpeedChart({ data, width, height, onHover, highlightIndex, colorProp }) {
  const canvasRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const padding = { top: 20, right: 15, bottom: 30, left: 45 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;

    const values = data.map(d => d.value);
    const maxVal = Math.max(...values, 1);
    const minVal = Math.min(...values, 0);
    const range = maxVal - minVal || 1;

    // Background
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#1a1a2e' : '#f8fafc';
    ctx.fillRect(0, 0, width, height);

    // Grid lines
    ctx.strokeStyle = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (chartH * i) / 4;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + chartW, y);
      ctx.stroke();

      ctx.fillStyle = theme.palette.mode === 'dark' ? '#888' : '#999';
      ctx.font = '10px Inter, sans-serif';
      ctx.textAlign = 'right';
      const label = (maxVal - (range * i) / 4).toFixed(0);
      ctx.fillText(label, padding.left - 5, y + 3);
    }

    // Draw line
    const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartH);
    gradient.addColorStop(0, '#ef4444');
    gradient.addColorStop(0.5, '#f59e0b');
    gradient.addColorStop(1, '#10b981');

    ctx.beginPath();
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 1.8;
    data.forEach((d, i) => {
      const x = padding.left + (i / (data.length - 1)) * chartW;
      const y = padding.top + chartH - ((d.value - minVal) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Fill under line
    const lastX = padding.left + chartW;
    const baseY = padding.top + chartH;
    ctx.lineTo(lastX, baseY);
    ctx.lineTo(padding.left, baseY);
    ctx.closePath();
    const fillGrad = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartH);
    fillGrad.addColorStop(0, 'rgba(239,68,68,0.15)');
    fillGrad.addColorStop(0.5, 'rgba(245,158,11,0.1)');
    fillGrad.addColorStop(1, 'rgba(16,185,129,0.05)');
    ctx.fillStyle = fillGrad;
    ctx.fill();

    // Highlight point
    if (highlightIndex !== null && highlightIndex >= 0 && highlightIndex < data.length) {
      const x = padding.left + (highlightIndex / (data.length - 1)) * chartW;
      const y = padding.top + chartH - ((data[highlightIndex].value - minVal) / range) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#6366f1';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = theme.palette.mode === 'dark' ? '#e0e0e0' : '#333';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(data[highlightIndex].value.toFixed(1), x, y - 10);
    }

    // X-axis label
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#888' : '#999';
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Point Index', padding.left + chartW / 2, height - 5);
  }, [data, width, height, highlightIndex, theme.palette.mode, colorProp]);

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const padding = 45;
    const chartW = width - padding - 15;
    const idx = Math.round(((x - padding) / chartW) * (data.length - 1));
    if (idx >= 0 && idx < data.length && onHover) onHover(idx);
  };

  return (
    <canvas
      ref={canvasRef}
      style={{ width, height, cursor: 'crosshair', borderRadius: 6 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => onHover && onHover(null)}
    />
  );
}

function TelemetryTripAnalyzer() {
  const theme = useTheme();
  const [features, setFeatures] = useState([]);
  const [error, setError] = useState('');
  const [colorProperty, setColorProperty] = useState('speed');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [speedThreshold, setSpeedThreshold] = useState(3);
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(400);

  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(() => {
        setChartWidth(containerRef.current?.clientWidth - 32 || 400);
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [features]);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse file');

      const fc = result.geoJson;
      let points = fc.features.filter(f => f.geometry?.type === 'Point');

      if (points.length === 0) {
        // Try extracting from LineString
        const lines = fc.features.filter(f => f.geometry?.type === 'LineString');
        if (lines.length > 0) {
          points = lines[0].geometry.coordinates.map((coord, i) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: coord },
            properties: { index: i, ...lines[0].properties },
          }));
        } else {
          throw new Error('No Point or LineString features found');
        }
      }

      setFeatures(points);
      setError('');
    } catch (err) {
      setError(`Failed to load: ${err.message}`);
    }
  }, []);

  const numericProperties = useMemo(() => {
    if (features.length === 0) return [];
    const props = features[0].properties || {};
    return Object.keys(props).filter(k => typeof props[k] === 'number');
  }, [features]);

  const tripStats = useMemo(() => {
    if (features.length < 2) return null;

    let totalDist = 0;
    const speeds = [];
    let minTs = null, maxTs = null;

    for (let i = 0; i < features.length; i++) {
      const f = features[i];
      const ts = f.properties?.timestamp || f.properties?.time || f.properties?.datetime || null;
      if (ts) {
        const d = new Date(ts);
        if (!minTs || d < minTs) minTs = d;
        if (!maxTs || d > maxTs) maxTs = d;
      }
      if (i > 0) {
        const dist = haversineDistance(features[i - 1].geometry.coordinates, f.geometry.coordinates);
        totalDist += dist;
        if (ts) {
          const prevTs = features[i - 1].properties?.timestamp || features[i - 1].properties?.time || null;
          if (prevTs) {
            speeds.push(calculateSpeed(features[i - 1].geometry.coordinates, f.geometry.coordinates, prevTs, ts));
          }
        }
        const propSpeed = f.properties?.speed ?? f.properties?.Speed;
        if (propSpeed !== undefined) speeds.push(propSpeed);
      }
    }

    const duration = minTs && maxTs ? (maxTs - minTs) / 1000 : 0;
    const avgSpeed = speeds.length > 0 ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0;
    const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0;

    return {
      totalDistance: totalDist,
      duration,
      avgSpeed,
      maxSpeed,
      pointCount: features.length,
      startTime: minTs?.toISOString() || '—',
      endTime: maxTs?.toISOString() || '—',
    };
  }, [features]);

  const stops = useMemo(() => {
    if (features.length < 2) return [];
    return detectStops(features, speedThreshold, 60);
  }, [features, speedThreshold]);

  const chartData = useMemo(() => {
    return features.map((f, i) => ({
      value: f.properties?.[colorProperty] ?? 0,
      index: i,
    }));
  }, [features, colorProperty]);

  const mapGeoJson = useMemo(() => {
    if (features.length === 0) return null;
    return { type: 'FeatureCollection', features };
  }, [features]);

  const styleFunction = useCallback((feature) => {
    const val = feature.properties?.[colorProperty];
    if (val === undefined) return { color: '#6366f1', weight: 2 };
    const allVals = features.map(f => f.properties?.[colorProperty] ?? 0);
    const max = Math.max(...allVals, 1);
    const min = Math.min(...allVals, 0);
    const ratio = max !== min ? (val - min) / (max - min) : 0;
    const r = Math.round(255 * ratio);
    const g = Math.round(255 * (1 - ratio));
    return { color: `rgb(${r}, ${g}, 80)`, fillColor: `rgb(${r}, ${g}, 80)`, fillOpacity: 0.8, radius: 4 };
  }, [colorProperty, features]);

  const pointToLayerFn = useCallback((feature, latlng) => {
    const val = feature.properties?.[colorProperty];
    const allVals = features.map(f => f.properties?.[colorProperty] ?? 0);
    const max = Math.max(...allVals, 1);
    const min = Math.min(...allVals, 0);
    const ratio = max !== min ? ((val ?? 0) - min) / (max - min) : 0;
    const r = Math.round(255 * ratio);
    const g = Math.round(255 * (1 - ratio));
    return L.circleMarker(latlng, {
      radius: 4, fillColor: `rgb(${r}, ${g}, 80)`, color: '#fff', weight: 0.5, fillOpacity: 0.85,
    });
  }, [colorProperty, features]);

  // Additional layers for stops
  const stopLayers = useMemo(() => {
    if (stops.length === 0) return [];
    const stopFeatures = stops.map((s, i) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: s.coordinate },
      properties: { name: `Stop ${i + 1}`, duration: s.duration },
    }));
    return [{
      geoJson: { type: 'FeatureCollection', features: stopFeatures },
      style: { color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.9, radius: 8 },
      pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
        radius: 8, fillColor: '#ef4444', color: '#fff', weight: 2, fillOpacity: 0.9,
      }).bindTooltip(`${feature.properties.name} (${Math.round(feature.properties.duration)}s)`, { direction: 'top' }),
    }];
  }, [stops]);

  const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(1)} m`;
  const fmtDuration = (s) => {
    if (s <= 0) return '—';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  const handleExportSummary = useCallback(() => {
    const summary = {
      tripStats,
      stops: stops.map(s => ({ ...s })),
      pointCount: features.length,
    };
    const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trip_analysis.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [tripStats, stops, features]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Telemetry Trip Analyzer | Map & Speed Chart | UmbrellaCo"
        description="Analyze telematics trip data with interactive map plotting and speed charts. Calculate distance, duration, and auto-detect stops from GPS traces."
        keywords="telemetry analyzer, trip analyzer, gps trace analysis, telematics visualizer, map speed chart"
        url="/spatial-tools/trip-analyzer"
        jsonLd={createSoftwareAppSchema(
          'Telemetry Trip Analyzer',
          'Analyze telematics trip data with interactive maps, speed charts, and stop detection.',
          'https://umbrellaco.in/spatial-tools/trip-analyzer',
          ['Trip Analysis', 'Stop Detection', 'Telemetry Charts']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Telemetry Trip Analyzer</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload trip data to visualize the route, view dynamic speed/altitude charts, and automatically detect stops and statistics.
        </Typography>
      </Box>
      <PanelGroup direction="vertical">
        {/* Top: Map + Controls */}
        <Panel defaultSize={65} minSize={35}>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={30} minSize={20}>
              <Box ref={containerRef} sx={{
                height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto',
                bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}`,
              }}>
                {/* Upload */}
                <Box sx={{ p: 2 }}>
                  <Button component="label" variant="contained" fullWidth size="small"
                    startIcon={<UploadFileIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
                    }}
                  >
                    {features.length > 0 ? `Trip Loaded (${features.length} pts)` : 'Upload Trip Data'}
                    <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={handleUpload} />
                  </Button>
                  {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                </Box>

                <Divider />

                {/* Trip Summary */}
                {tripStats && (
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                      <SpeedIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      <Typography variant="subtitle2" fontWeight={700}>Trip Summary</Typography>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      {[
                        { label: 'Distance', value: fmtDist(tripStats.totalDistance) },
                        { label: 'Duration', value: fmtDuration(tripStats.duration) },
                        { label: 'Avg Speed', value: `${tripStats.avgSpeed.toFixed(1)} km/h` },
                        { label: 'Max Speed', value: `${tripStats.maxSpeed.toFixed(1)} km/h` },
                        { label: 'Points', value: tripStats.pointCount },
                        { label: 'Stops', value: stops.length },
                      ].map(({ label, value }) => (
                        <Paper key={label} variant="outlined" sx={{
                          p: 1, textAlign: 'center',
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.03)',
                        }}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={700}>{value}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                <Divider />

                {/* Color Property Selector */}
                {numericProperties.length > 0 && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Color By Property</Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Property</InputLabel>
                      <Select value={colorProperty} label="Property" onChange={(e) => setColorProperty(e.target.value)}>
                        {numericProperties.map(p => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                {/* Stop Detection */}
                {features.length > 0 && (
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                      <PlaceIcon fontSize="small" sx={{ color: '#ef4444' }} />
                      <Typography variant="subtitle2" fontWeight={700}>Stop Detection</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" sx={{ minWidth: 80 }}>Speed &lt;</Typography>
                      <Slider value={speedThreshold} onChange={(e, v) => setSpeedThreshold(v)}
                        min={1} max={20} step={1} size="small" sx={{ flex: 1 }} />
                      <Typography variant="caption" fontWeight={600} sx={{ minWidth: 45 }}>{speedThreshold} km/h</Typography>
                    </Stack>
                    {stops.length > 0 && (
                      <Stack spacing={0.5} sx={{ mt: 1, maxHeight: '15vh', overflowY: 'auto' }}>
                        {stops.map((s, i) => (
                          <Chip key={i} size="small"
                            label={`Stop ${i + 1}: ${fmtDuration(s.duration)} (${s.pointCount} pts)`}
                            sx={{ bgcolor: '#ef444422', color: '#ef4444', fontWeight: 600, justifyContent: 'flex-start' }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}

                {/* Export */}
                {tripStats && (
                  <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, mt: 'auto' }}>
                    <Button variant="outlined" fullWidth size="small" startIcon={<DownloadIcon />}
                      onClick={handleExportSummary}>
                      Export Trip Report
                    </Button>
                  </Box>
                )}
              </Box>
            </Panel>

            <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

            <Panel defaultSize={70} minSize={40}>
              <Box sx={{ height: '100%', width: '100%' }}>
                {mapGeoJson ? (
                  <MapView
                    mapId="trip-analyzer-map"
                    geoJsonData={mapGeoJson}
                    styleFunction={styleFunction}
                    pointToLayerFn={pointToLayerFn}
                    additionalLayers={stopLayers}
                  />
                ) : (
                  <Box sx={{
                    height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
                  }}>
                    <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center', p: 4 }}>
                      <TimelineIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                      <Typography variant="h6" color="text.secondary">Upload trip data</Typography>
                      <Typography variant="body2" color="text.disabled">
                        Supports GeoJSON, CSV, TSV — auto-detects columns
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Panel>
          </PanelGroup>
        </Panel>

        <PanelResizeHandle style={{ height: '5px', background: theme.palette.divider, cursor: 'row-resize' }} />

        {/* Bottom: Chart */}
        <Panel defaultSize={35} minSize={15}>
          <Box sx={{
            height: '100%', bgcolor: 'background.paper', display: 'flex',
            flexDirection: 'column', overflow: 'hidden',
          }}>
            {chartData.length > 1 ? (
              <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexShrink: 0 }}>
                  <TimelineIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {colorProperty} over Trip
                  </Typography>
                  {hoveredIndex !== null && (
                    <Chip size="small" label={`Point ${hoveredIndex}: ${chartData[hoveredIndex]?.value?.toFixed(2) ?? '—'}`}
                      sx={{ fontFamily: 'monospace' }} />
                  )}
                </Stack>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <SpeedChart
                    data={chartData}
                    width={chartWidth}
                    height={180}
                    onHover={setHoveredIndex}
                    highlightIndex={hoveredIndex}
                    colorProp={colorProperty}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Load a trip to see the telemetry chart
                </Typography>
              </Box>
            )}
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default TelemetryTripAnalyzer;
